import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppActionMenu from '../../../components/AppActionMenu';
import ToolsButton from '../../../components/buttons/ToolsButton';
import useSharedMasterListScreen from './hooks/useSharedMasterListScreen';

import AppPagination from '../../../components/AppPagination';
import { appToast } from '../../../components/AppToast';

const SharedListTable = ({ config }) => {
  const { state, actions } = useSharedMasterListScreen(config);
  const navigate = useNavigate();

  const hasUnderColumn = config.id === 'ac-groups' || config.columns?.includes('Under');

  return (
    <div className="flex flex-col gap-6">
      
      {/* 1. Filters Panel (Search + Segmented Tabs Control + Actions) */}
      <section className="rounded-2xl border border-outline-variant/35 bg-surface-container-low p-4 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3 w-full">
          {/* Left side: Search & View Mode segmented tabs */}
          <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0">
            
            {/* Merged Connected Search Input Group Wrapper */}
            <div className="flex items-center w-full max-w-md rounded-xl border border-outline-variant/30 bg-surface-container-lowest overflow-hidden transition-all focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary/40 shadow-xs shrink-0">
              {hasUnderColumn && (
                <div className="border-r border-outline-variant/30 shrink-0">
                  <select
                    value={state.searchTarget || 'Name'}
                    onChange={(e) => actions.setSearchTarget(e.target.value)}
                    className="h-10 border-0 rounded-none bg-transparent px-3 text-xs font-bold text-on-surface focus:outline-none"
                  >
                    <option value="Name">Name</option>
                    <option value="Under">Under</option>
                  </select>
                </div>
              )}

              {/* Search Input Area with Icon */}
              <div className="relative flex-1 flex items-center">
                <span className="material-symbols-outlined absolute left-3.5 text-on-surface-variant text-base pointer-events-none select-none">
                  search
                </span>
                <input
                  type="text"
                  value={state.searchText}
                  onChange={(e) => actions.setSearchText(e.target.value)}
                  placeholder={`Search ${hasUnderColumn ? (state.searchTarget || 'Name').toLowerCase() : 'records'}...`}
                  className="h-10 w-full bg-transparent pl-10 pr-4 text-xs text-on-surface outline-none placeholder:text-on-surface-variant border-0"
                />
              </div>
            </div>

            {/* Right-aligned within left section: View Mode Tabs */}
            <div className="ml-auto flex flex-wrap items-center gap-3 shrink-0">
              <div className="flex rounded-xl bg-surface-container-high p-1 gap-1">
                {['Active', 'Archive', 'All'].map(tab => {
                  const isActive = state.activeTab === tab;
                  return (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => actions.setActiveTab(tab)}
                      className={isActive 
                        ? 'bg-primary text-on-primary shadow-sm rounded-lg px-4 py-1.5 text-xs font-bold transition-all shrink-0'
                        : 'text-on-surface-variant hover:text-on-surface px-4 py-1.5 text-xs font-bold transition-colors shrink-0'
                      }
                    >
                      {tab}
                    </button>
                  );
                })}
              </div>
            </div>

          </div>

          {/* Right side Actions (Divider + Tools dropdown button, conditional) */}
          {config.tools && config.tools.length > 0 && (
            <div className="flex items-center gap-3 pl-3 border-l border-outline-variant/25 md:border-gray-200 shrink-0 select-none">
              <ToolsButton
                tools={config.tools}
                onSelect={(key) => {
                  const item = config.tools.find((t) => t.key === key);
                  const label = item ? item.label : key;
                  appToast.info(`Executing tool: ${label}...`);
                }}
              />
            </div>
          )}

        </div>
      </section>

      {/* 2. The Data Table Card */}
      <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest overflow-hidden shadow-sm flex flex-col">
        
        {/* Header Row */}
        <div className={`grid ${hasUnderColumn ? 'grid-cols-[1.5fr_1.5fr_140px_120px_80px]' : 'grid-cols-[1fr_160px_120px_90px]'} px-5 py-3.5 border-b border-outline-variant/25 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant select-none`}>
          <span>Name</span>
          {hasUnderColumn && <span>Under</span>}
          <span>Status</span>
          <span>Created Date</span>
          <span className="text-right">Actions</span>
        </div>

        {/* Data Rows / States */}
        <div className="flex flex-col">
          {state.isLoading ? (
            <div className="px-6 py-16 text-center text-sm text-on-surface-variant font-medium">
              Loading entries...
            </div>
          ) : state.rows.length === 0 ? (
            <div className="px-6 py-16 text-center text-sm text-on-surface-variant font-medium">
              No entries found matching filters.
            </div>
          ) : (
            state.rows.map((row) => (
              <div 
                key={row.id} 
                className={`grid ${hasUnderColumn ? 'grid-cols-[1.5fr_1.5fr_140px_120px_80px]' : 'grid-cols-[1fr_160px_120px_90px]'} items-center px-5 py-3.5 border-b border-outline-variant/10 hover:bg-surface-container-low/40 transition-colors last:border-b-0`}
              >
                {/* Name */}
                <span className="font-bold text-on-surface select-text">
                  {row.name}
                </span>

                {/* Under Column (if applicable) */}
                {hasUnderColumn && (
                  <div>
                    <span className="inline-flex items-center rounded-lg bg-surface-container-low px-2.5 py-1 text-xs font-semibold text-secondary select-text">
                      {row.under || 'Main'}
                    </span>
                  </div>
                )}

                {/* Status chip with dot */}
                <div className="flex select-none">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${
                      row.status === 'Active' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-surface-variant text-on-surface-variant'
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${
                      row.status === 'Active' ? 'bg-emerald-500' : 'bg-outline'
                    }`} />
                    {row.status}
                  </span>
                </div>

                {/* Created Date */}
                <span className="text-xs font-medium text-on-surface-variant font-body tracking-tight tabular-nums select-text">
                  {row.createdDate}
                </span>

                {/* Action - three-dot menu */}
                <div className="flex justify-end select-none">
                  <AppActionMenu
                    triggerLabel=""
                    triggerIcon="more_vert"
                    showChevron={false}
                    triggerClassName="rounded-lg p-1 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent"
                    items={[
                      { key: 'edit', label: 'Edit', icon: 'edit' },
                      { key: 'archive', label: row.status === 'Active' ? 'Archive' : 'Unarchive', icon: row.status === 'Active' ? 'archive' : 'unarchive' },
                      { key: 'delete', label: 'Delete', icon: 'delete' },
                    ]}
                    onSelect={(key) => {
                      if (key === 'edit') {
                        navigate(`/masters/${config.id}/entry?id=${row.id}`);
                      } else if (key === 'archive') {
                        actions.handleArchive(row.id);
                      } else if (key === 'delete') {
                        actions.handleDelete(row.id);
                      }
                    }}
                  />
                </div>
              </div>
            ))
          )}
        </div>

        {/* 3. Footer Control */}
        <div className="flex items-center justify-between bg-surface-container px-6 py-4 text-sm text-on-surface-variant font-semibold select-none border-t border-outline-variant/10">
          <span>{state.rangeLabel}</span>
          
          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.goToPage}
            onPrevious={actions.goToPreviousPage}
            onNext={actions.goToNextPage}
            canPrevious={state.canPrevious}
            canNext={state.canNext}
          />
        </div>

      </div>
    </div>
  );
};

export default SharedListTable;
